import React, { useState, useRef, useCallback, useEffect } from 'react';
import { tz } from '../../utils/region';
import ScheduleSendMenu from '../ScheduleSendMenu';
import { fmtBkk } from '../../utils/bkkTime';
import { useTranslation } from 'react-i18next';
import EmailTagInput from '../EmailTagInput';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { TEXT_COLORS, IndentParagraph, shiftIndent } from '../RichTextEditor';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { ComposeRestore, Email } from '../../types';
import { makePasteHandlers, genId, wrapQuote } from '../../utils/compose';
import { sendWarnings, SendWarning } from '../../utils/sendChecks';
import { useAutoSaveDraft } from '../../hooks/useAutoSaveDraft';
import { useAutoCc } from '../../hooks/useAutoCc';
import AIReplyModal from '../Modals/AIReplyModal';
import SendCheckModal from '../Modals/SendCheckModal';

type ComposeMode = 'reply' | 'replyAll' | 'forward';

// Convert AI plain-text output to simple HTML paragraphs
function textToHtml(text: string): string {
  const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return String(text || '').split(/\n{2,}/).map(p => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');
}


const LANG_OPTIONS = [
  { code: 'en', label: 'English' }, { code: 'zh', label: '中文' },
  { code: 'th', label: 'Thai' }, { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' }, { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' }, { code: 'es', label: 'Spanish' },
  { code: 'pt', label: 'Portuguese' }, { code: 'ar', label: 'Arabic' },
];

// EmailTagInput is imported from ../EmailTagInput (shared with ComposeNew)
// Re-export a local alias so existing JSX references (TagInput) still compile.
const TagInput = EmailTagInput;

// ── Toolbar button ─────────────────────────────────────────────────────────
function TBtn({ onClick, active, title, icon }: { onClick: () => void; active?: boolean; title: string; icon: string }) {
  return (
    <button onMouseDown={e => { e.preventDefault(); onClick(); }} title={title} aria-label={title}
      style={{ width: 26, height: 26, borderRadius: 'var(--radius-sm)', border: 'none', background: active ? 'var(--c-gray-100)' : 'transparent', color: active ? 'var(--text-primary)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
      <i className={`ti ${icon}`} style={{ fontSize: 13 }} aria-hidden />
    </button>
  );
}

// ── HTML iframe preview ────────────────────────────────────────────────────
function HtmlPreview({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(80);
  const onLoad = useCallback(() => {
    const doc = ref.current?.contentDocument || ref.current?.contentWindow?.document;
    if (doc) setHeight(Math.max(60, (doc.documentElement.scrollHeight || doc.body.scrollHeight) + 10));
  }, []);
  return <iframe ref={ref} srcDoc={html} onLoad={onLoad} sandbox="allow-same-origin" title="translation-preview"
    style={{ border: 'none', width: '100%', height, display: 'block', background: 'transparent' }} />;
}

// ── Preview send modal ─────────────────────────────────────────────────────
function PreviewModal({
  toList, ccList, subject, translatedHtml, attachments, targetLang, langName,
  onSend, onClose, sending,
}: {
  toList: string[]; ccList: string[]; subject: string;
  translatedHtml: string; attachments: { file: File; id: string }[];
  targetLang: string; langName: string;
  onSend: () => void; onClose: () => void; sending: boolean;
}) {
  const [iframeHeight, setIframeHeight] = useState(300);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const onLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
    if (doc) setIframeHeight(Math.max(200, (doc.documentElement.scrollHeight || doc.body.scrollHeight) + 20));
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Modal header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="ti ti-eye" style={{ fontSize: 16, color: 'var(--accent)' }} aria-hidden />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Preview Email</span>
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--accent-bg)', color: 'var(--accent-text)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)', fontWeight: 500 }}>
              <i className="ti ti-language" style={{ fontSize: 10, marginRight: 3 }} aria-hidden />{langName}
            </span>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4 }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>

        {/* Email meta */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'var(--c-gray-50)' }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}><span style={{ color: 'var(--text-tertiary)', fontWeight: 600, marginRight: 6 }}>To:</span><span style={{ color: 'var(--text-primary)' }}>{toList.join(', ')}</span></div>
          {ccList.length > 0 && <div style={{ fontSize: 12, marginBottom: 4 }}><span style={{ color: 'var(--text-tertiary)', fontWeight: 600, marginRight: 6 }}>CC:</span><span style={{ color: 'var(--text-primary)' }}>{ccList.join(', ')}</span></div>}
          <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-tertiary)', fontWeight: 600, marginRight: 6 }}>Subject:</span><span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{subject}</span></div>
          {attachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {attachments.map(a => (
                <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '2px 8px', fontSize: 11 }}>
                  <i className="ti ti-paperclip" style={{ fontSize: 11 }} aria-hidden />{a.file.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Email body preview */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {translatedHtml
            ? <iframe ref={iframeRef} srcDoc={translatedHtml} onLoad={onLoad} sandbox="allow-same-origin" title="preview"
                style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', width: '100%', height: iframeHeight, display: 'block' }} />
            : <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                No translation preview available — the email will be sent in your original language.
              </div>
          }
        </div>

        {/* Modal footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <button onClick={onSend} disabled={sending} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}>
            <i className="ti ti-send" style={{ fontSize: 13 }} aria-hidden />
            {sending ? 'Sending…' : 'Confirm & Send'}
          </button>
          <button onClick={onClose} disabled={sending} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            Back to edit
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <i className="ti ti-wand" style={{ fontSize: 11, color: 'var(--accent)' }} aria-hidden />
            Sending translated version to customer
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Confirm dialog ─────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }: { message: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', padding: '24px 28px', width: 340, textAlign: 'center' }}>
        <i className="ti ti-alert-triangle" style={{ fontSize: 32, color: '#E24B4A', marginBottom: 12, display: 'block' }} aria-hidden />
        <p style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 20, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onCancel} style={{ padding: '8px 20px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: '8px 20px', fontSize: 13, borderRadius: 'var(--radius-md)', border: 'none', background: '#E24B4A', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Composer ──────────────────────────────────────────────────────────
// `restore`: Undo send opened this reply / forward again (components/UndoSend) — its fields start as
// they were when Send was pressed; the quote and the forwarded attachments still come from `email`.
export default function Composer({ email, mode, onClose, restore }: { email: Email; mode: ComposeMode; onClose: () => void; restore?: ComposeRestore }) {
  const { t } = useTranslation();
  const { uiLang, accounts, activeAccountId, showToast, incComposing, decComposing, loadEmails } = useStore();

  // Pause background refresh while composing (protects current work). The user's Undo send choice
  // comes with the Email layout; loadUndoSend asks for it if this page hasn't had it (Leads, Starred).
  useEffect(() => {
    incComposing();
    useStore.getState().loadUndoSend();
    return () => decComposing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reply All must keep everyone: the sender + the original "To" recipients + the original "Cc",
  // minus only the mailbox you're replying FROM (so you don't email yourself). The old code set To to
  // just email.from and silently dropped every address that was in the original "To" — including our
  // own other mailboxes like info@pandabot.ai.
  const normAddr = (e: string) => (e || '').trim().toLowerCase();
  const selfEmail = normAddr(accounts.find(a => a.id === (email?.accountId || activeAccountId))?.email || '');
  const dedupeExcl = (arr: string[], alsoExclude: string[] = []) => {
    const skip = new Set([selfEmail, ...alsoExclude.map(normAddr)].filter(Boolean));
    const seen = new Set<string>(); const out: string[] = [];
    for (const e of (arr || [])) { const n = normAddr(e); if (!n || skip.has(n) || seen.has(n)) continue; seen.add(n); out.push(e); }
    return out;
  };
  const initTo = mode === 'forward' ? []
    : mode === 'replyAll' ? dedupeExcl([email.from.email, ...(email.to || [])])
      : [email.from.email];
  const initCc = mode === 'replyAll' ? dedupeExcl(email.cc || [], initTo) : [];
  const initSubject = (mode === 'forward' ? 'Fwd: ' : 'Re: ') + email.subject;
  // Where the fields start: the email as Undo send gave it back, else a fresh reply / forward.
  // Replies/forwards go out from the mailbox the original landed in (falls back to the active
  // account). May be a mailbox the user doesn't own — lead replies are authorized server-side.
  const init: ComposeRestore = restore || {
    draftId: null, accountId: email?.accountId || activeAccountId || accounts[0]?.id || '',
    to: initTo, cc: initCc, bcc: [], subject: initSubject, html: '', preview: '',
    composedLang: uiLang, targetLang: uiLang, files: [],
  };

  const [fromAccountId, setFromAccountId] = useState(init.accountId);
  const [toList, setToList] = useState<string[]>(init.to);
  const [ccTyped, setCcTyped] = useState<string[]>(init.cc);
  const [bccTyped, setBccTyped] = useState<string[]>(init.bcc);
  const [subject, setSubject] = useState(init.subject);
  const [showCc, setShowCc] = useState(init.cc.length > 0);
  const [showBcc, setShowBcc] = useState(init.bcc.length > 0);
  // The user's Auto CC / BCC for the From mailbox join as ordinary chips after the typed ones (see
  // hooks/useAutoCc) — changing From swaps them — and open their row; not into an email Undo send
  // gave back (its chips are already in, and one the user removed stays removed). ccList / bccList
  // are what is shown, previewed, saved and sent.
  const { autoCc, autoBcc, dropAuto } = useAutoCc({
    enabled: !restore,
    fromAccountId,
    fromEmail: accounts.find(a => a.id === fromAccountId)?.email || email?.accountEmail || '',
    to: toList, cc: ccTyped, bcc: bccTyped,
  });
  const ccList = [...ccTyped, ...autoCc];
  const bccList = [...bccTyped, ...autoBcc];
  useEffect(() => { if (autoCc.length) setShowCc(true); }, [autoCc.length]);
  useEffect(() => { if (autoBcc.length) setShowBcc(true); }, [autoBcc.length]);
  const [attachments, setAttachments] = useState<{ file: File; id: string }[]>(() => init.files.map(file => ({ file, id: genId() })));
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Pre-send reminder on screen (blank subject / "attached" but nothing attached) and what
  // "Send anyway" carries on with: opening the preview, or the send itself.
  const [sendCheck, setSendCheck] = useState<{ warnings: SendWarning[]; next: 'preview' | 'send' | 'schedule'; at?: number } | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);

  // Send As language — defaults to the language you WRITE in (i.e. auto-translation OFF).
  // Picking a different "Send as" language is the explicit opt-in for translation.
  const [targetLang, setTargetLang] = useState<string>(init.targetLang);
  // Language the editor content is currently written in (uiLang normally; switches
  // to the customer's language when AI generates content directly in it).
  const [composedLang, setComposedLang] = useState<string>(init.composedLang);
  const [showAI, setShowAI] = useState(false);

  // Signature — loaded separately, NOT put in TipTap (TipTap strips inline styles)
  const [signature, setSignature] = useState<string>('');

  // Translation preview
  const [translationPreview, setTranslationPreview] = useState(init.preview);
  const [translating, setTranslating] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLInputElement>(null);

  const needsTranslation = targetLang !== composedLang;
  // useEditor's onUpdate closure is created once — read live language state through a ref.
  const transRef = useRef<{ needs: boolean; from: string; to: string }>({ needs: false, from: uiLang, to: uiLang });
  transRef.current = { needs: needsTranslation, from: composedLang, to: targetLang };

  // Build the quoted/forwarded original as standalone HTML (kept OUTSIDE TipTap so
  // images, tables and styles survive — TipTap's schema would strip them).
  const quoteHtml = (() => {
    const dateStr = email.date ? new Intl.DateTimeFormat('en-GB', { timeZone: tz(), day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(email.date) : '';
    // Header values are TEXT going into HTML — escape them, or "Name <addr@x.com>" loses the
    // address: the mail client parses <addr@x.com> as an unknown tag and swallows it entirely
    // (owner 2026-08-27: a forwarded enquiry reached Calem with no way to contact the client).
    const esc = (s: any) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const fromStr = esc(email.from.name ? `${email.from.name} <${email.from.email}>` : email.from.email);
    if (mode === 'forward') {
      return `
        <p style="color:#666;font-size:13px;margin:0 0 8px;">
          <strong>---------- Forwarded message ----------</strong><br>
          <strong>From:</strong> ${fromStr}<br>
          <strong>Date:</strong> ${dateStr}<br>
          <strong>Subject:</strong> ${esc(email.subject)}<br>
          <strong>To:</strong> ${esc(email.to.join(', '))}
        </p>
        <blockquote style="margin:8px 0 0 0;padding:8px 12px;border-left:3px solid #ccc;color:#333;">
          ${email.bodyOriginal}
        </blockquote>`;
    }
    return `
      <p style="color:#666;font-size:13px;margin:0 0 4px;">On ${dateStr}, ${fromStr} wrote:</p>
      <blockquote style="margin:4px 0 0 0;padding:8px 12px;border-left:3px solid #1D9E75;color:#333;">
        ${email.bodyOriginal}
      </blockquote>`;
  })();

  // Paste / drop handlers: images inline, other files become attachments
  const pasteHandlers = makePasteHandlers((file) =>
    setAttachments(prev => [...prev, { file, id: genId() }])
  );

  const editor = useEditor({
    // Colour and indent go out as inline styles, which the recipient's mail program shows. IndentParagraph
    // takes the place of StarterKit's own paragraph (two extensions of one name make TipTap warn).
    extensions: [StarterKit.configure({ paragraph: false }), IndentParagraph, Underline, TextStyle, Color,
      Link.configure({ openOnClick: false }), Image, Placeholder.configure({ placeholder: 'Write your message…' })],
    content: init.html,  // a fresh message, or the one Undo send stopped; the quote is shown read-only below
    editorProps: { handlePaste: pasteHandlers.handlePaste, handleDrop: pasteHandlers.handleDrop },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const userText = html.replace(/<[^>]+>/g, '').trim();
      if (!userText) { setTranslationPreview(''); return; }
      if (!transRef.current.needs) { setTranslationPreview(html); return; }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setTranslating(true);
        try {
          const { translated } = await api.translateText(html, transRef.current.from, transRef.current.to);
          setTranslationPreview(translated);
        } catch {}
        setTranslating(false);
      }, 3000);
    },
  });

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Load signature separately (don't put in TipTap — it strips inline styles)
  useEffect(() => {
    if (!fromAccountId) return;
    api.getSignature(fromAccountId)
      .then(({ signature: sig }) => setSignature(sig || ''))
      .catch(() => {});
  }, [fromAccountId]);

  // ── Auto-save draft (offline-safe, survives crashes) ──
  const { clearDraft, saveNow, draftIdRef } = useAutoSaveDraft({
    active: !sending,
    initialDraftId: init.draftId,
    getSnapshot: () => ({
      accountId: fromAccountId,
      to: toList, cc: ccList, bcc: bccList,
      subject: subject || '(No subject)',
      // Persist user message + quoted original (so the draft keeps the full thread)
      bodyHtml: (editor?.getHTML() || '') + wrapQuote(quoteHtml),
      bodyLang: composedLang,
      targetLang,
    }),
  });

  const handleImg = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const reader = new FileReader();
    reader.onload = ev => editor.chain().focus().setImage({ src: ev.target?.result as string }).run();
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    setAttachments(prev => [...prev, ...files.map(f => ({ file: f, id: Math.random().toString(36).slice(2) }))]);
    e.target.value = '';
  };

  // Assemble the outgoing body: user message + signature + quoted/forwarded original
  const buildOutgoingHtml = () => {
    const html = editor?.getHTML() || '';
    const sigBlock = signature ? `<hr style="border:none;border-top:1px solid #eee;margin:16px 0">${signature}` : '';
    const quoteBlock = quoteHtml ? `<br><br>${quoteHtml}` : '';
    return html + sigBlock + quoteBlock;
  };

  // True when a pre-send reminder is now showing: the caller stops, and "Send anyway" resumes
  // `next`. A forward also carries the original email's attachments, so those count as attached.
  const remindBeforeSend = (next: 'preview' | 'send' | 'schedule', at?: number) => {
    const warnings = sendWarnings(subject, editor?.getHTML() || '',
      attachments.length + (mode === 'forward' ? email.attachments.length : 0));
    if (warnings.length) setSendCheck({ warnings, next, at });
    return warnings.length > 0;
  };
  // Schedule send (Bugify "Schedule Send Emails", Calem P0): the same checks and payload as Send
  // Now, parked on the server until `at` (ms). Own personal mailbox only — the ▾ is shown only then.
  const doSchedule = async (at: number, checked = false) => {
    if (inFlight.current) return;
    const userText = (editor?.getHTML() || '').replace(/<[^>]+>/g, '').trim();
    if (!userText && attachments.length === 0) { showToast('Please write a message before scheduling'); return; }
    if (!fromAccountId) { showToast('No account selected'); return; }
    if (![...toList, ...ccTyped, ...bccTyped].some(a => String(a || '').trim())) {
      try { await saveNow(); } catch { /* keep going — the toast still tells them */ }
      showToast('Add a recipient before scheduling — your draft has been saved');
      return;
    }
    if (!checked && remindBeforeSend('schedule', at)) return;
    const payload = {
      accountId: fromAccountId,
      to: toList, cc: ccList, bcc: bccList,
      subject, bodyHtml: buildOutgoingHtml(),
      bodyLang: composedLang,
      targetLang: targetLang,
      files: attachments.map(a => a.file),
      forwardFromEmailId: mode === 'forward' ? email.id : undefined,
      replyToEmailId: (mode === 'reply' || mode === 'replyAll') ? email.id : undefined,
      sendAt: Math.floor(at / 1000),
    };
    const subjSnap = subject || '(No subject)';
    inFlight.current = true;
    try { await saveNow(); } catch { /* draft is a best-effort safety net */ }
    showToast('Scheduling…');
    onClose();
    api.scheduleEmail(payload).then(async () => {
      try { await clearDraft(); } catch {}
      const st = useStore.getState();
      st.showToast(t('eml_sched_done', { time: fmtBkk(at, uiLang) }));
      st.loadEmails({ silent: true });
    }).catch((e: any) => {
      useStore.getState().setBlockingError(
        `"${subjSnap}"\n\n${e?.message || e}\n\nYour message is saved in Drafts — open it there to send again.`
      );
    });
  };
  const canSchedule = !!accounts.find(a => a.id === fromAccountId)?.personal;

  // Async send: capture the payload, close the composer IMMEDIATELY, deliver in the background —
  // after the user's Undo send seconds, if any (store.queueSend). Success → quiet toast + silent list
  // merge. Failure → BLOCKING error dialog; the auto-saved draft is kept, so the message can be
  // reopened from Drafts and retried.
  // `checked` = the pre-send reminders were already passed (Send anyway, or the preview's Confirm).
  // One submission per composer: a second click during the pre-close draft save used to post twice.
  const inFlight = useRef(false);
  const doSend = async (checked = false) => {
    if (inFlight.current) return;
    const userText = (editor?.getHTML() || '').replace(/<[^>]+>/g, '').trim();
    if (!userText && attachments.length === 0) {
      showToast('Please write a message before sending'); return;
    }
    if (!fromAccountId) { showToast('No account selected'); return; }
    // Never post an addressless send: it cannot succeed, and the failure used to lose the message
    // because the draft is only written to the server AFTER the send (owner, 2026-08-22). Auto CC /
    // BCC alone doesn't count: a forward sent before To is filled would reach only those copies.
    if (![...toList, ...ccTyped, ...bccTyped].some(a => String(a || '').trim())) {
      try { await saveNow(); } catch { /* keep going — the toast still tells them */ }
      showToast('Add a recipient before sending — your draft has been saved');
      return;
    }
    if (!checked && remindBeforeSend('send')) return;
    const html = editor?.getHTML() || '';
    const payload = {
      accountId: fromAccountId,
      to: toList, cc: ccList, bcc: bccList,
      subject, bodyHtml: buildOutgoingHtml(),
      bodyLang: composedLang,
      targetLang: targetLang,
      files: attachments.map(a => a.file),
      // Forward re-attaches the original email's file attachments
      forwardFromEmailId: mode === 'forward' ? email.id : undefined,
      // Reply threading — keeps the reply in the same conversation (and Lead).
      replyToEmailId: (mode === 'reply' || mode === 'replyAll') ? email.id : undefined,
    };
    const subjSnap = subject || '(No subject)';
    inFlight.current = true;
    try { await saveNow(); } catch { /* draft is a best-effort safety net */ }
    onClose();
    useStore.getState().queueSend({
      subject: subjSnap, payload, clearDraft, draftId: () => draftIdRef.current,
      // What Undo opens again: this reply / forward as it is now, attachments included.
      restore: { accountId: fromAccountId, to: toList, cc: ccList, bcc: bccList, subject, html, preview: translationPreview, composedLang, targetLang, files: payload.files },
      reopen: { kind: 'reply', email, mode },
    });
  };

  const handlePreviewSend = () => {
    const html = editor?.getHTML() || '';
    if (!html.replace(/<[^>]+>/g, '').trim() && attachments.length === 0) {
      showToast('Please write a message first'); return;
    }
    if (remindBeforeSend('preview')) return;
    setShowPreview(true);
  };

  const handleSendNow = () => {
    doSend();
  };

  const handleSaveDraft = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      await saveNow();
      showToast('Draft saved');
      loadEmails();
      onClose();
    } catch (e: any) {
      showToast('Failed to save draft: ' + e.message);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleDelete = () => setShowDeleteConfirm(true);

  const fmtBytes = (b: number) => b < 1024 ? b + 'B' : b < 1048576 ? (b / 1024).toFixed(0) + 'KB' : (b / 1048576).toFixed(1) + 'MB';
  const langName = (code: string) => t(`langNames.${code}` as any, { defaultValue: code });
  const modeLabel = mode === 'reply' ? 'Reply' : mode === 'replyAll' ? 'Reply All' : 'Forward';
  const modeIcon = mode === 'forward' ? 'ti-corner-up-right' : mode === 'replyAll' ? 'ti-corner-up-left-double' : 'ti-corner-up-left';

  return (
    <>
      {/* Modal overlay */}
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '10px 16px', borderBottom: '2px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className={`ti ${modeIcon}`} style={{ fontSize: 15, color: 'var(--accent)' }} aria-hidden />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{modeLabel}</span>
          </div>
          {/* Send As selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 500 }}>Send as</span>
            <select
              value={targetLang}
              onChange={e => {
                const newLang = e.target.value;
                setTargetLang(newLang);
                // Re-translate the current message for the preview
                const html = editor?.getHTML() || '';
                const userText = html.replace(/<[^>]+>/g, '').trim();
                if (!userText || newLang === composedLang) { setTranslationPreview(html); return; }
                setTranslating(true);
                api.translateText(html, composedLang, newLang)
                  .then(r => setTranslationPreview(r.translated))
                  .catch(() => {})
                  .finally(() => setTranslating(false));
              }}
              style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--c-gray-50)', fontSize: 12, color: 'var(--text-primary)', padding: '4px 8px', cursor: 'pointer', outline: 'none', fontWeight: 500 }}
            >
              {LANG_OPTIONS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            {needsTranslation && (
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                · Writing in <b style={{ color: 'var(--text-secondary)' }}>{langName(composedLang)}</b>
                {' → '}
                <b style={{ color: 'var(--accent)' }}>{langName(targetLang)}</b>
              </span>
            )}
          </div>
          {/* Close keeps the auto-saved draft. Use Delete to discard it. */}
          <button onClick={() => { loadEmails(); onClose(); }} title="Close (keeps draft)" style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4 }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {/* Recipients */}
          <div style={{ padding: '0 16px' }}>
            {/* From */}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)', minHeight: 36 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', width: 36, flexShrink: 0, textAlign: 'right', marginRight: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>From</span>
              <select value={fromAccountId} onChange={e => setFromAccountId(e.target.value)}
                style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: 'var(--text-primary)', padding: '5px 0', cursor: 'pointer' }}>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name} &lt;{a.email}&gt;</option>)}
                {fromAccountId && !accounts.some(a => a.id === fromAccountId) && (
                  <option value={fromAccountId}>{email?.accountEmail || 'Original mailbox'}</option>
                )}
              </select>
            </div>
            <TagInput label="To" values={toList} onAdd={v => setToList(p => [...p, v])} onRemove={v => setToList(p => p.filter(x => x !== v))} />
            {showCc && <TagInput label="CC" values={ccList} onAdd={v => setCcTyped(p => [...p, v])} onRemove={v => { if (autoCc.includes(v)) dropAuto(v); else setCcTyped(p => p.filter(x => x !== v)); }} />}
            {showBcc && <TagInput label="BCC" values={bccList} onAdd={v => setBccTyped(p => [...p, v])} onRemove={v => { if (autoBcc.includes(v)) dropAuto(v); else setBccTyped(p => p.filter(x => x !== v)); }} />}
            {/* Each + stays until its own row is open — an auto-opened CC row must not hide + BCC */}
            {(!showCc || !showBcc) && (
              <div style={{ display: 'flex', gap: 6, padding: '3px 0 3px 46px' }}>
                {!showCc && <button onClick={() => setShowCc(true)} style={{ border: 'none', background: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>+ CC</button>}
                {!showBcc && <button onClick={() => setShowBcc(true)} style={{ border: 'none', background: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>+ BCC</button>}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)', minHeight: 32 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', width: 36, flexShrink: 0, textAlign: 'right', marginRight: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subj</span>
              <input ref={subjectRef} value={subject} onChange={e => setSubject(e.target.value)} style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: 'var(--text-primary)', padding: '4px 0', fontWeight: 500 }} />
            </div>
          </div>

          {/* Pane 1: Your message */}
          <div style={{ borderBottom: needsTranslation ? '1px solid var(--border)' : 'none' }}>
            <div style={{ padding: '4px 14px', display: 'flex', alignItems: 'center', gap: 4, background: 'var(--c-gray-50)', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your message</span>
              <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>· {langName(composedLang)}</span>
              <div style={{ flex: 1 }} />
              {/* ONE merged AI button (2026-07-29): instruction optional — empty = full auto from the KB */}
              <button onMouseDown={e => { e.preventDefault(); setShowAI(true); }} title="AI — leave the instruction empty for a fully automatic knowledge-base reply"
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700, marginRight: 4 }}>
                <i className="ti ti-sparkles" style={{ fontSize: 12 }} aria-hidden />AI
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TBtn onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')} title="Bold" icon="ti-bold" />
                <TBtn onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic')} title="Italic" icon="ti-italic" />
                <TBtn onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive('underline')} title="Underline" icon="ti-underline" />
                <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 2px' }} />
                <TBtn onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList')} title="Bullet list" icon="ti-list" />
                <TBtn onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive('orderedList')} title="Numbered list" icon="ti-list-numbers" />
                <TBtn onClick={() => editor && shiftIndent(editor, -1)} title={t('eml_fmt_outdent')} icon="ti-indent-decrease" />
                <TBtn onClick={() => editor && shiftIndent(editor, 1)} title={t('eml_fmt_indent')} icon="ti-indent-increase" />
                <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 2px' }} />
                {TEXT_COLORS.map(c => (
                  <button key={c} type="button" title={t('eml_fmt_color', { color: c })} onMouseDown={e => { e.preventDefault(); editor?.chain().focus().setColor(c).run(); }}
                    style={{ width: 15, height: 15, borderRadius: '50%', background: c, border: '1px solid var(--border-strong)', cursor: 'pointer', padding: 0, margin: '0 1px' }} />
                ))}
                <TBtn onClick={() => editor?.chain().focus().unsetColor().run()} title={t('eml_fmt_no_color')} icon="ti-ban" />
                <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 2px' }} />
                <TBtn onClick={() => { const url = window.prompt('Enter URL'); if (url && editor) editor.chain().focus().setLink({ href: url }).run(); }} active={editor?.isActive('link')} title="Link" icon="ti-link" />
                <TBtn onClick={() => imgRef.current?.click()} title="Insert image" icon="ti-photo" />
                <TBtn onClick={() => fileRef.current?.click()} title="Attach file (or paste)" icon="ti-paperclip" />
                <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={handleFiles} />
                <input ref={imgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImg} />
              </div>
            </div>
            <div style={{ padding: '10px 16px', minHeight: 140 }}>
              <EditorContent editor={editor} />
            </div>
            {/* Signature block — rendered outside TipTap to preserve inline styles */}
            {signature && (
              <div style={{ padding: '0 16px 10px', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <i className="ti ti-signature" style={{ fontSize: 10 }} aria-hidden />
                  Signature
                </div>
                <div dangerouslySetInnerHTML={{ __html: signature }} style={{ fontSize: 13 }} />
              </div>
            )}
            {/* Quoted / forwarded original — read-only, full fidelity (images, tables) */}
            {quoteHtml && (
              <div style={{ padding: '0 16px 10px', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <i className={`ti ${mode === 'forward' ? 'ti-corner-up-right' : 'ti-quote'}`} style={{ fontSize: 10 }} aria-hidden />
                  {mode === 'forward' ? 'Forwarded message (included)' : 'Original message (quoted)'}
                </div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#fff' }}>
                  <HtmlPreview html={quoteHtml} />
                </div>
                {mode === 'forward' && email.attachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {email.attachments.map((a: any) => (
                      <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--accent-bg)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)', borderRadius: 'var(--radius-sm)', padding: '3px 8px', fontSize: 11, color: 'var(--accent-text)' }}>
                        <i className="ti ti-paperclip" style={{ fontSize: 11 }} aria-hidden />{a.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {attachments.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 14px 8px' }}>
                {attachments.map(a => (
                  <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '3px 7px', fontSize: 11 }}>
                    <i className="ti ti-file" style={{ fontSize: 11, color: 'var(--accent)' }} aria-hidden />
                    <span style={{ maxWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.file.name}</span>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>{fmtBytes(a.file.size)}</span>
                    <button onClick={() => setAttachments(p => p.filter(x => x.id !== a.id))} style={{ border: 'none', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 0, display: 'flex' }}>
                      <i className="ti ti-x" style={{ fontSize: 10 }} aria-hidden />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Pane 2: Auto-translated preview */}
          {needsTranslation && (
            <div>
              <div style={{ padding: '4px 14px', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--accent-bg)', borderBottom: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                <i className="ti ti-wand" style={{ fontSize: 11, color: 'var(--accent)' }} aria-hidden />
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Auto-translated</span>
                <span style={{ fontSize: 10, color: 'var(--accent-text)' }}>· {langName(targetLang)} — this is what will be sent to the customer</span>
                {translating && <span style={{ fontSize: 10, color: 'var(--accent)', fontStyle: 'italic', marginLeft: 4 }}>Translating…</span>}
                {attachments.length > 0 && <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 'auto' }}>+ {attachments.length} attachment{attachments.length > 1 ? 's' : ''} included</span>}
              </div>
              <div style={{ padding: '8px 14px', minHeight: 60, background: 'color-mix(in srgb, var(--accent) 3%, transparent)' }}>
                {translationPreview
                  ? <HtmlPreview html={translationPreview} />
                  : <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                      {editor?.getText().trim() ? 'Translation will appear here after 3 seconds…' : 'Type your message above to see translation preview'}
                    </div>
                }
              </div>
              {attachments.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 14px 8px', background: 'color-mix(in srgb, var(--accent) 3%, transparent)', borderTop: '1px solid color-mix(in srgb, var(--accent) 10%, transparent)' }}>
                  {attachments.map(a => (
                    <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'color-mix(in srgb, var(--accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)', borderRadius: 'var(--radius-sm)', padding: '3px 7px', fontSize: 11, color: 'var(--accent-text)' }}>
                      <i className="ti ti-file" style={{ fontSize: 11, color: 'var(--accent)' }} aria-hidden />
                      <span style={{ maxWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.file.name}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Action bar ── */}
        <div style={{ padding: '10px 14px', borderTop: '2px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-surface)', flexShrink: 0, flexWrap: 'wrap' }}>
          {/* Preview & Send */}
          <button onClick={handlePreviewSend} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <i className="ti ti-eye" style={{ fontSize: 13 }} aria-hidden />
            Preview &amp; Send
          </button>

          {/* Send Now — with the Send menu ▾ joined to it: Undo send for every sender, Schedule send
              when the sender is a personal mailbox */}
          <span style={{ display: 'flex', alignItems: 'stretch' }}>
            <button onClick={handleSendNow} disabled={sending} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'transparent', color: 'var(--accent)', border: '1.5px solid var(--accent)', borderRadius: 'var(--radius-md) 0 0 var(--radius-md)', fontSize: 12, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}>
              <i className="ti ti-send" style={{ fontSize: 13 }} aria-hidden />
              {sending ? 'Sending…' : 'Send Now'}
            </button>
            <ScheduleSendMenu disabled={sending} uiLang={uiLang} canSchedule={canSchedule} onPick={at => doSchedule(at)} />
          </span>

          {/* Save Draft */}
          <button onClick={handleSaveDraft} disabled={savingDraft} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: savingDraft ? 'not-allowed' : 'pointer', opacity: savingDraft ? 0.7 : 1 }}>
            <i className="ti ti-device-floppy" style={{ fontSize: 13 }} aria-hidden />
            {savingDraft ? 'Saving…' : 'Save Draft'}
          </button>

          {/* Delete */}
          <button onClick={handleDelete} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: 'transparent', color: '#E24B4A', border: '1px solid rgba(226,75,74,0.3)', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: 'pointer', marginLeft: 'auto' }}>
            <i className="ti ti-trash" style={{ fontSize: 13 }} aria-hidden />
            Delete
          </button>

          {needsTranslation && (
            <span style={{ fontSize: 10, color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 3, width: '100%', paddingTop: 2 }}>
              <i className="ti ti-wand" style={{ fontSize: 10, color: 'var(--accent)' }} aria-hidden />
              The translated version ({langName(targetLang)}) will be sent to the customer
            </span>
          )}
        </div>
      </div>
      </div>{/* end modal overlay */}

      {/* Preview modal */}
      {showPreview && (
        <PreviewModal
          toList={toList} ccList={ccList} subject={subject}
          translatedHtml={
            ((needsTranslation ? translationPreview : (editor?.getHTML() || '')) || '') +
            (signature ? `<hr style="border:none;border-top:1px solid #eee;margin:16px 0">${signature}` : '') +
            (quoteHtml ? `<br><br>${quoteHtml}` : '')
          }
          attachments={mode === 'forward'
            ? [...attachments, ...email.attachments.map((a: any) => ({ file: { name: a.name } as File, id: a.id }))]
            : attachments}
          targetLang={targetLang} langName={langName(targetLang)}
          onSend={() => doSend(true)} onClose={() => setShowPreview(false)}
          sending={sending}
        />
      )}

      {/* Pre-send reminder — Go back to fix the email, or Send anyway */}
      {sendCheck && (
        <SendCheckModal
          warnings={sendCheck.warnings}
          onBack={() => {
            if (sendCheck.warnings.some(w => w.kind === 'no_subject')) subjectRef.current?.focus();
            setSendCheck(null);
          }}
          onSendAnyway={() => {
            setSendCheck(null);
            if (sendCheck.next === 'preview') setShowPreview(true);
            else if (sendCheck.next === 'schedule' && sendCheck.at) doSchedule(sendCheck.at, true);
            else doSend(true);
          }}
        />
      )}

      {/* Delete confirm — discards the auto-saved draft */}
      {showDeleteConfirm && (
        <ConfirmDialog
          message="Discard this reply and delete the draft? This cannot be undone."
          onConfirm={async () => {
            setShowDeleteConfirm(false);
            await clearDraft();
            loadEmails();
            onClose();
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* AI Reply assistant */}
      {showAI && (
        <AIReplyModal
          uiLang={uiLang}
          defaultTargetLang={targetLang}
          langOptions={LANG_OPTIONS}
          emailId={mode === 'forward' ? undefined : email?.id}
          accountEmail={accounts.find(a => a.id === fromAccountId)?.email || ''}
          toEmail={toList[0] || ''}
          onClose={() => setShowAI(false)}
          onInsert={(text, lang) => {
            editor?.chain().focus().insertContent(textToHtml(text)).run();
            // The content is now in the customer's language → no re-translation on send
            setComposedLang(lang);
            setTargetLang(lang);
            setTranslationPreview('');
            setShowAI(false);
          }}
        />
      )}
    </>
  );
}
